import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button, Input, Alert } from "../components/UI";
import { Mail, Lock, Leaf } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const [form, setForm] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.email) e.email = "Please enter your email";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Email not valid";
    if (!form.password) e.password = "Please enter your password";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setErrors({});
    setApiError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate(from, { replace: true });
    } catch (err) {
      setApiError(err?.response?.data?.message || "Login failed. Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => setForm({ email: "demo@agrihealth.com", password: "demo1234" });

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-earth-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-slide-up">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-600 text-white rounded-2xl shadow-lg mb-4">
            <Leaf size={32} />
          </div>
          <h1 className="text-3xl font-900 text-brand-800">Welcome Back!</h1>
          <p className="text-brand-500 font-600 mt-1">Sign in to check your crops and animals</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-brand-100 p-6 lg:p-8">
          {apiError && <div className="mb-4"><Alert type="error" onDismiss={() => setApiError("")}>{apiError}</Alert></div>}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Input
              label="📧 Email Address"
              type="email"
              placeholder="yourname@email.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              error={errors.email}
              icon={Mail}
            />
            <Input
              label="🔒 Password"
              type="password"
              placeholder="Enter your password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              error={errors.password}
              icon={Lock}
            />

            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-sm font-700 text-brand-500 hover:text-brand-700">
                Forgot password?
              </Link>
            </div>

            <Button type="submit" size="lg" loading={loading} className="w-full mt-1">
              🚀 Sign In
            </Button>
          </form>

          <div className="mt-4 pt-4 border-t border-brand-100">
            <button
              onClick={fillDemo}
              className="w-full text-center text-sm font-700 text-brand-500 hover:text-brand-700 bg-brand-50 hover:bg-brand-100 rounded-xl py-2.5 transition-all"
            >
              👆 Use Demo Account (demo@agrihealth.com)
            </button>
          </div>
        </div>

        <p className="text-center mt-6 text-brand-600 font-700">
          New farmer?{" "}
          <Link to="/register" className="text-brand-700 underline font-800 hover:text-brand-900">
            Create Free Account
          </Link>
        </p>

        {/* Trust badges */}
        <div className="flex justify-center gap-6 mt-8 text-xs text-brand-400 font-600">
          <span>🔒 Secure Login</span>
          <span>🌍 Works Offline*</span>
          <span>📱 Mobile Friendly</span>
        </div>
      </div>
    </div>
  );
}
